// src/context/CookieConsentContext.jsx

import { createContext, useContext, useEffect, useState } from "react";

const CookieConsentContext = createContext(null);

const STORAGE_KEY = "ninetyone_cookie_consent";

const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);

      if (saved) {
        const parsed = JSON.parse(saved);

        setConsent({
          ...DEFAULT_CONSENT,
          ...parsed,
        });
      } else {
        setShowBanner(true);
      }
    } catch (error) {
      console.error("Failed to load cookie consent:", error);
      setShowBanner(true);
    }
  }, []);

  const saveConsent = (preferences) => {
    const finalConsent = {
      ...DEFAULT_CONSENT,
      ...preferences,
      necessary: true,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(finalConsent));

    setConsent(finalConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectOptional = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const dismissBanner = () => {
    const noConsent = {
      necessary: false,
      analytics: false,
      marketing: false,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(noConsent));

    setConsent(noConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  const resetConsent = () => {
    localStorage.removeItem(STORAGE_KEY);

    setConsent(null);
    setShowBanner(true);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        showSettings,
        acceptAll,
        rejectOptional,
        dismissBanner,
        saveConsent,
        openSettings,
        closeSettings,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);

  if (!context) {
    throw new Error(
      "useCookieConsent must be used inside CookieConsentProvider",
    );
  }

  return context;
}
